import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const GUEST_KEY = 'ovr_cart_v1_guest';
const cartKeyFor = (userId) => userId ? `ovr_cart_v1_${userId}` : GUEST_KEY;

export const TOURN_INFO = {
  blitz: { name: 'OVR BLITZ',         teamFee: '$80',  faFee: '$20'  },
  szn6:  { name: 'OVR CUP SZN VI',    teamFee: '$200', faFee: '$40'  },
  carleton: { name: 'OVR × CARLETON FUNDRAISER', teamFee: '$240', faFee: '$40' },
};

function loadFromKey(key) {
  try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const keyRef = useRef(cartKeyFor(userId));

  const [items, setItems]                   = useState(() => loadFromKey(keyRef.current));
  const [open, setOpen]                     = useState(false);
  const [faConflicts, setFaConflicts]       = useState({});
  const [checkingConflicts, setChecking]    = useState(false);

  // When auth state changes: swap to the right key, and migrate guest cart on login.
  useEffect(() => {
    const newKey = cartKeyFor(userId);
    if (userId) {
      // Login: merge any guest cart into the user's cart
      const guestItems = loadFromKey(GUEST_KEY);
      const userItems  = loadFromKey(newKey);
      const merged = [...userItems, ...guestItems];
      try { localStorage.setItem(newKey, JSON.stringify(merged)); } catch (e) {}
      localStorage.removeItem(GUEST_KEY);
      keyRef.current = newKey;
      setItems(merged);
      // If they had a guest cart waiting, surface the cart drawer now
      if (guestItems.length > 0) setOpen(true);
    } else {
      // Logout: switch to guest cart
      keyRef.current = GUEST_KEY;
      setItems(loadFromKey(GUEST_KEY));
    }
    setFaConflicts({});
  }, [userId]);

  const persist = (next) => {
    setItems(next);
    try { localStorage.setItem(keyRef.current, JSON.stringify(next)); } catch (e) { /* ignore quota */ }
  };

  const addItem = useCallback((item) => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? `cart_${crypto.randomUUID()}`
      : `cart_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    setItems(prev => {
      const next = [...prev, { ...item, id, addedAt: Date.now() }];
      try { localStorage.setItem(keyRef.current, JSON.stringify(next)); } catch (e) { /* quota - in-memory only */ }
      return next;
    });
    // Only auto-open the cart drawer for authenticated users.
    // Guests get the auth modal first; the drawer surfaces after login (see useEffect).
    if (userId) setOpen(true);
  }, [userId]);

  const removeItem = useCallback((id) => {
    setItems(prev => {
      const next = prev.filter(i => i.id !== id);
      try { localStorage.setItem(keyRef.current, JSON.stringify(next)); } catch (e) { /* ignore quota */ }
      return next;
    });
    setFaConflicts(prev => { const n = { ...prev }; delete n[id]; return n; });
  }, []);

  const updateItem = useCallback((id, updates) => {
    setItems(prev => {
      const next = prev.map(i => i.id === id ? { ...i, ...updates } : i);
      try { localStorage.setItem(keyRef.current, JSON.stringify(next)); } catch (e) { /* ignore quota */ }
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    persist([]);
    setFaConflicts({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkFaConflicts = useCallback(async (cartItems) => {
    const teamItems = cartItems.filter(i => i.type === 'tournament_team' && i.faPlayers?.length > 0);
    if (!teamItems.length) { setFaConflicts({}); return; }

    setChecking(true);
    const conflicts = {};

    for (const item of teamItems) {
      const faIds = (item.faPlayers || []).map(fa => fa.fa_id).filter(Boolean);
      if (!faIds.length) continue;

      const { data } = await supabase
        .from('tournament_free_agents')
        .select('id, assigned_team_id')
        .in('id', faIds);

      const availMap = new Map((data || []).map(r => [r.id, r]));
      // Only flag as conflict if the FA exists in DB but is claimed — skip stale IDs silently
      const stolen = item.faPlayers.filter(fa =>
        fa.fa_id && availMap.has(fa.fa_id) && availMap.get(fa.fa_id).assigned_team_id
      );

      if (stolen.length > 0) {
        conflicts[item.id] = stolen.map(fa =>
          `${fa.firstName || ''} ${fa.lastName || ''}`.trim() || 'A free agent'
        );
      }
    }

    setFaConflicts(conflicts);
    setChecking(false);
  }, []);

  const count = items.length;

  return (
    <CartContext.Provider value={{
      items, count, open, setOpen,
      addItem, removeItem, updateItem, clearCart,
      faConflicts, checkFaConflicts, checkingConflicts,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
